import { getSlugFromPage } from '../../utils/routeUtils';

const INSTAGRAM_APP_ID = import.meta.env.VITE_INSTAGRAM_APP_ID || '';

/** Chat Direct — Instagram Login only (CAPI events usam Meta Ads + instagram_manage_events). */
const INSTAGRAM_SCOPES = [
    'instagram_business_basic',
    'instagram_business_manage_messages',
].join(',');

const OAUTH_STATE_KEY = 'adlitics_instagram_oauth_state';
const OAUTH_ORG_KEY = 'adlitics_instagram_oauth_org_id';
const OAUTH_REDIRECT_KEY = 'adlitics_instagram_oauth_redirect_uri';
const CONNECTING_KEY = 'adlitics_instagram_connecting';
const PENDING_CODE_KEY = 'adlitics_instagram_oauth_pending_code';

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export class InstagramOAuthRedirectPending extends Error {
    constructor() {
        super('Redirecionando para autenticação Instagram');
        this.name = 'InstagramOAuthRedirectPending';
    }
}

export type InstagramOAuthCallback = {
    code: string;
    organizationId: string;
    redirectUri: string;
};

/** Redirect URI canônico — sem barra final. Deve ser idêntico no authorize e no token exchange. */
export const getInstagramOAuthRedirectUri = (): string => {
    const slug = getSlugFromPage('connections');
    return `${window.location.origin}/${slug}`.replace(/\/$/, '');
};

const requireInstagramAppId = (): string => {
    if (!INSTAGRAM_APP_ID) {
        throw new Error(
            'Configure VITE_INSTAGRAM_APP_ID (Meta → API do Instagram → Configuração da API com login do Instagram).'
        );
    }
    return INSTAGRAM_APP_ID;
};

const buildOAuthState = (organizationId: string): { state: string; nonce: string } => {
    const nonce = crypto.randomUUID();
    return { nonce, state: `${nonce}.${organizationId}` };
};

const parseOAuthState = (state: string): { nonce: string; organizationId: string } | null => {
    const dot = state.indexOf('.');
    if (dot <= 0) return null;
    const nonce = state.slice(0, dot);
    const organizationId = state.slice(dot + 1);
    if (!UUID_RE.test(nonce) || !UUID_RE.test(organizationId)) return null;
    return { nonce, organizationId };
};

const persistOAuthStart = (nonce: string, organizationId: string, redirectUri: string): void => {
    try {
        localStorage.setItem(OAUTH_STATE_KEY, nonce);
        localStorage.setItem(OAUTH_ORG_KEY, organizationId);
        localStorage.setItem(OAUTH_REDIRECT_KEY, redirectUri);
    } catch {
        /* private mode */
    }
    sessionStorage.setItem(OAUTH_STATE_KEY, nonce);
    sessionStorage.setItem(OAUTH_ORG_KEY, organizationId);
    sessionStorage.setItem(OAUTH_REDIRECT_KEY, redirectUri);
    sessionStorage.setItem(CONNECTING_KEY, 'true');
};

const readStoredRedirectUri = (): string | null =>
    sessionStorage.getItem(OAUTH_REDIRECT_KEY) || localStorage.getItem(OAUTH_REDIRECT_KEY);

const readStoredNonce = (): string | null =>
    sessionStorage.getItem(OAUTH_STATE_KEY) || localStorage.getItem(OAUTH_STATE_KEY);

/** Meta anexa "#_" ao redirect — remover se vier colado no code. */
const normalizeOAuthCode = (code: string): string => code.replace(/#_?$/, '');

const isInstagramConnectionsReturn = (): boolean => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('code') || !params.has('state')) return false;

    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const igSlug = getSlugFromPage('connections');
    if (path !== `/${igSlug}` && !path.endsWith(`/${igSlug}`)) return false;

    if (
        sessionStorage.getItem('adlitics_meta_oauth_state') ||
        localStorage.getItem('adlitics_meta_oauth_state')
    ) {
        return false;
    }
    if (sessionStorage.getItem('adlitics_google_ads_oauth_state')) return false;

    return true;
};

export const startInstagramOAuthRedirect = (organizationId: string): void => {
    const redirectUri = getInstagramOAuthRedirectUri();
    const { state, nonce } = buildOAuthState(organizationId);
    const appId = requireInstagramAppId();

    sessionStorage.removeItem(PENDING_CODE_KEY);
    persistOAuthStart(nonce, organizationId, redirectUri);

    const params = new URLSearchParams({
        client_id: appId,
        redirect_uri: redirectUri,
        scope: INSTAGRAM_SCOPES,
        response_type: 'code',
        state,
        enable_fb_login: '0',
    });
    window.location.assign(`https://www.instagram.com/oauth/authorize?${params.toString()}`);
};

function mapOAuthError(error: string, description?: string | null): Error {
    const text = (description || '').toLowerCase();
    if (error === 'access_denied') {
        return new Error('Autorização cancelada. Aceite as permissões de Instagram Direct.');
    }
    if (error === 'redirect_uri_mismatch' || text.includes('redirect_uri')) {
        const uri = readStoredRedirectUri() || getInstagramOAuthRedirectUri();
        return new Error(
            `URL de retorno não confere. Cadastre exatamente esta URL no Meta → Login do Instagram: ${uri}`
        );
    }
    return new Error(description || 'Falha na autorização com o Instagram.');
}

export const clearInstagramOAuthUrlParams = (): void => {
    const url = new URL(window.location.href);
    ['code', 'state', 'error', 'error_reason', 'error_description'].forEach((key) => {
        url.searchParams.delete(key);
    });
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
};

/** Remove code da URL e pending — OAuth codes são single-use. */
export const consumeInstagramOAuthCallback = (callback: InstagramOAuthCallback): void => {
    sessionStorage.removeItem(PENDING_CODE_KEY);
    clearInstagramOAuthUrlParams();
    sessionStorage.setItem(PENDING_CODE_KEY, JSON.stringify(callback));
};

export const clearInstagramOAuthSession = (): void => {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_ORG_KEY);
    sessionStorage.removeItem(OAUTH_REDIRECT_KEY);
    sessionStorage.removeItem(CONNECTING_KEY);
    sessionStorage.removeItem(PENDING_CODE_KEY);
    try {
        localStorage.removeItem(OAUTH_STATE_KEY);
        localStorage.removeItem(OAUTH_ORG_KEY);
        localStorage.removeItem(OAUTH_REDIRECT_KEY);
    } catch {
        /* private mode */
    }
};

export const readInstagramOAuthCallback = (): InstagramOAuthCallback | null => {
    const redirectUri = readStoredRedirectUri() || getInstagramOAuthRedirectUri();

    const pendingRaw = sessionStorage.getItem(PENDING_CODE_KEY);
    if (pendingRaw) {
        try {
            const pending = JSON.parse(pendingRaw) as InstagramOAuthCallback;
            if (pending.code && pending.organizationId) {
                return { ...pending, redirectUri: pending.redirectUri || redirectUri };
            }
        } catch {
            sessionStorage.removeItem(PENDING_CODE_KEY);
        }
    }

    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (error) {
        throw mapOAuthError(error, params.get('error_description') || params.get('error_reason'));
    }

    const codeRaw = params.get('code');
    const state = params.get('state');
    if (!codeRaw || !state) return null;
    const code = normalizeOAuthCode(codeRaw);

    const path = window.location.pathname.replace(/\/$/, '') || '/';
    const metaSlug = getSlugFromPage('tracking');
    if (path === `/${metaSlug}` || path.endsWith(`/${metaSlug}`)) {
        return null;
    }

    if (
        sessionStorage.getItem('adlitics_meta_oauth_state') ||
        localStorage.getItem('adlitics_meta_oauth_state')
    ) {
        return null;
    }
    if (sessionStorage.getItem('adlitics_google_ads_oauth_state')) return null;

    const parsed = parseOAuthState(state);
    const savedNonce = readStoredNonce();

    if (parsed) {
        if (savedNonce !== parsed.nonce) {
            persistOAuthStart(parsed.nonce, parsed.organizationId, redirectUri);
        }
        const pending = { code, organizationId: parsed.organizationId, redirectUri };
        sessionStorage.setItem(PENDING_CODE_KEY, JSON.stringify(pending));
        sessionStorage.setItem(CONNECTING_KEY, 'true');
        return pending;
    }

    if (!savedNonce || state !== savedNonce) {
        clearInstagramOAuthUrlParams();
        throw new Error('Sessão de login inválida. Inicie a conexão novamente.');
    }

    const organizationId =
        sessionStorage.getItem(OAUTH_ORG_KEY) || localStorage.getItem(OAUTH_ORG_KEY);
    if (!organizationId) {
        clearInstagramOAuthUrlParams();
        throw new Error('Organização não encontrada. Inicie a conexão novamente.');
    }

    const pending = { code, organizationId, redirectUri };
    sessionStorage.setItem(PENDING_CODE_KEY, JSON.stringify(pending));
    return pending;
};

export const hasPendingInstagramOAuthCallback = (): boolean => {
    if (sessionStorage.getItem(PENDING_CODE_KEY)) return true;
    return isInstagramConnectionsReturn();
};
